/**
 * Receipt Translations Service
 * 
 * Provides localized strings for receipt printing based on system language.
 * Used by silentPrintService.js, receiptBuilder.js, and printService.js
 */

// Receipt translations by language
const RECEIPT_TRANSLATIONS = {
  es: {
    orderNumber: "Pedido",
    date: "Fecha",
    customer: "Cliente",
    phone: "Tel",
    channel: "Canal",
    table: "Mesa",
    delivery: "Entrega",
    orderItems: "ARTICULOS DEL PEDIDO",
    subtotal: "Subtotal",
    discount: "Descuento",
    tax: "IVA",
    taxBase: "Base",
    deliveryFee: "Envio",
    serviceCharge: "Servicio",
    total: "TOTAL",
    payment: "Pago",
    change: "Cambio",
    notes: "Obs",
    thankYou: "Gracias por su preferencia!",
    scanQR: "Escanea y mira nuestro menu",
    nif: "NIF",
    sources: {
      online: "Online",
      whatsapp: "WhatsApp",
      counter: "Balcão",
      table: "Mesa",
      delivery: "Delivery",
      phone: "Telefono",
      app: "App",
      ifood: "iFood",
      ubereats: "Uber Eats",
      pos: "TPV",
      glovo: "Glovo",
      rappi: "Rappi"
    }
  },
  pt: {
    orderNumber: "Pedido",
    date: "Data",
    customer: "Cliente",
    phone: "Tel",
    channel: "Canal",
    table: "Mesa",
    delivery: "Entrega",
    orderItems: "ITENS DO PEDIDO",
    subtotal: "Subtotal",
    discount: "Desconto",
    tax: "IVA",
    taxBase: "Base",
    deliveryFee: "Taxa de entrega",
    serviceCharge: "Servico",
    total: "TOTAL",
    payment: "Pagamento",
    change: "Troco",
    notes: "Obs",
    thankYou: "Obrigado pela preferencia!",
    scanQR: "Escaneie e veja nosso menu",
    nif: "NIF",
    sources: {
      online: "Online",
      whatsapp: "WhatsApp",
      counter: "Balcao",
      table: "Mesa",
      delivery: "Delivery",
      phone: "Telefone",
      app: "App",
      ifood: "iFood",
      ubereats: "Uber Eats",
      pos: "PDV",
      glovo: "Glovo",
      rappi: "Rappi"
    }
  },
  en: {
    orderNumber: "Order",
    date: "Date",
    customer: "Customer",
    phone: "Phone",
    channel: "Channel",
    table: "Table",
    delivery: "Delivery",
    orderItems: "ORDER ITEMS",
    subtotal: "Subtotal",
    discount: "Discount",
    tax: "Tax",
    taxBase: "Net",
    deliveryFee: "Delivery fee",
    serviceCharge: "Service",
    total: "TOTAL",
    payment: "Payment",
    change: "Change",
    notes: "Notes",
    thankYou: "Thank you for your visit!",
    scanQR: "Scan to see our menu",
    nif: "Tax ID",
    sources: {
      online: "Online",
      whatsapp: "WhatsApp",
      counter: "Counter",
      table: "Table",
      delivery: "Delivery",
      phone: "Phone",
      app: "App",
      ifood: "iFood",
      ubereats: "Uber Eats",
      pos: "POS",
      glovo: "Glovo",
      rappi: "Rappi"
    }
  }
};

/**
 * Get current language from i18n or localStorage
 */
export function getCurrentLanguage() {
  // Try to get from localStorage (where i18n stores it)
  const saved = localStorage.getItem('kaiso_language');
  if (saved && ['es', 'pt', 'en'].includes(saved)) {
    return saved;
  }
  
  // Try to get from restaurant locale
  try {
    const restaurantData = localStorage.getItem('kaiso_restaurant_locale');
    if (restaurantData) {
      const { locale } = JSON.parse(restaurantData);
      if (locale) {
        const lang = locale.split('-')[0];
        if (['es', 'pt', 'en'].includes(lang)) {
          return lang;
        }
      }
    }
  } catch {}
  
  // Default to Spanish (system is used in Spain)
  return 'es';
}

/**
 * Get receipt translations for current language
 * @param {string} [lang] - Optional language override
 * @returns {Object} Translation strings
 */
export function getReceiptTranslations(lang) {
  const language = lang || getCurrentLanguage();
  return RECEIPT_TRANSLATIONS[language] || RECEIPT_TRANSLATIONS.es;
}

/**
 * Get translated source label
 * @param {string} source - Source key (online, pos, table, etc)
 * @param {string} [lang] - Optional language override
 * @returns {string} Translated source label
 */
export function getSourceLabel(source, lang) {
  const t = getReceiptTranslations(lang);
  return t.sources[source] || source || 'Online';
}

/**
 * Format receipt text with translations
 * This is used for ESC/POS thermal printer commands
 */
export function getReceiptText(key, lang) {
  const t = getReceiptTranslations(lang);
  return t[key] || key;
}

export default {
  getReceiptTranslations,
  getSourceLabel,
  getReceiptText,
  getCurrentLanguage,
  RECEIPT_TRANSLATIONS
};
